/**
 * Product boundary configuration options
 */
export interface BoundaryConfig {
  readonly visible?: boolean;
  readonly label?: string;
  readonly className?: string;
  readonly labelClassName?: string;
}

/**
 * Boundary state interface
 */
interface BoundaryState {
  readonly visible: boolean;
  readonly label: string;
}

/**
 * BoundaryManager API for controlling product boundary display
 */
export interface BoundaryManagerAPI {
  readonly setVisible: (visible: boolean) => void;
  readonly setLabel: (label: string) => void;
  readonly update: () => void;
  readonly getState: () => Readonly<BoundaryState>;
  readonly destroy: () => void;
}

/**
 * Creates a BoundaryManager for handling product boundary display
 * @param container - Container element for the boundary DIV
 * @param config - Boundary configuration options
 * @returns BoundaryManager API
 */
export const createBoundaryManager = (
  container: HTMLElement,
  config: BoundaryConfig = {}
): BoundaryManagerAPI => {
  console.log("🔲 Creating BoundaryManager");

  let isDestroyed = false;

  // Configuration with defaults
  const boundaryConfig = {
    visible: config.visible !== false, // Default to true
    label: config.label || "test-site",
    className: config.className || "ui-helpers__boundary",
    labelClassName: config.labelClassName || "ui-helpers__boundary-label",
  };

  // Boundary state
  let state: BoundaryState = {
    visible: boundaryConfig.visible,
    label: boundaryConfig.label,
  };

  // DOM elements for boundary
  let boundaryDiv: HTMLElement | null = null;
  let boundaryLabel: HTMLElement | null = null;

  /**
   * Create boundary DIV with label
   */
  const createBoundaryDiv = (): void => {
    if (boundaryDiv) return; // Already created

    // Create boundary container
    boundaryDiv = document.createElement("div");
    boundaryDiv.className = boundaryConfig.className;

    // Create label
    boundaryLabel = document.createElement("div");
    boundaryLabel.className = boundaryConfig.labelClassName;
    boundaryLabel.textContent = state.label;

    // Add label to boundary div
    boundaryDiv.appendChild(boundaryLabel);

    // Add to container
    container.appendChild(boundaryDiv);
  };

  /**
   * Update boundary DIV position to match product container
   */
  const updateBoundaryPosition = (): void => {
    if (!boundaryDiv) {
      if (state.visible) {
        createBoundaryDiv();
      } else {
        return;
      }
    }

    if (!boundaryDiv) return;

    // Find the product container element
    const productContainer = document.querySelector(
      ".stage__product-container"
    ) as HTMLElement;

    if (!productContainer) {
      boundaryDiv.style.display = "none";
      return;
    }

    const containerRect = productContainer.getBoundingClientRect();
    const stageRect = container.getBoundingClientRect();

    // Calculate container position and size relative to stage
    const containerLeft = containerRect.left - stageRect.left;
    const containerTop = containerRect.top - stageRect.top;
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // Position boundary div to match product container exactly
    boundaryDiv.style.display = state.visible ? "block" : "none";
    boundaryDiv.style.left = containerLeft + "px";
    boundaryDiv.style.top = containerTop + "px";
    boundaryDiv.style.width = containerWidth + "px";
    boundaryDiv.style.height = containerHeight + "px";
  };

  /**
   * Set boundary visibility
   */
  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    state = { ...state, visible };
    updateBoundaryPosition();
  };

  /**
   * Set boundary label text
   */
  const setLabel = (label: string): void => {
    if (isDestroyed) return;

    state = { ...state, label };

    // Update label element if it exists
    if (boundaryLabel) {
      boundaryLabel.textContent = label;
    }
  };

  /**
   * Update boundary position (public method for external calls)
   */
  const update = (): void => {
    if (isDestroyed) return;

    updateBoundaryPosition();
  };

  /**
   * Get current state
   */
  const getState = (): Readonly<BoundaryState> => ({ ...state });

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    if (boundaryDiv && boundaryDiv.parentNode) {
      boundaryDiv.parentNode.removeChild(boundaryDiv);
    }

    boundaryDiv = null;
    boundaryLabel = null;
  };

  // Initialize - create boundary if visible
  if (state.visible) {
    createBoundaryDiv();
  }

  console.log("BoundaryManager initialized");

  return {
    setVisible,
    setLabel,
    update,
    getState,
    destroy,
  };
};
